/**
 * Signing in as a location, at `/l/<code>`.
 *
 * The page whoever is at the counter reaches from the link written on the wall.
 * It names the business so they can see they are in the right place, prefills
 * the username from the link, and asks for the password.
 *
 * Deliberately a separate page from the ordinary sign-in rather than a tab on
 * it: a shared counter credential and a person's own account are different
 * things, and offering both on one form invites people to type the wrong one
 * into the wrong field.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { LocationSignInContext } from '@cenox/shared';
import { useAuth } from '../../lib/auth';
import { api, ApiError } from '../../lib/api';
import { safeNext, withNext } from '../../lib/intent';
import { Button, Input, Spinner } from '../../components/ui';
import { AuthLayout } from './AuthLayout';
import { LOCATION_HOME } from '../../lib/locationSession';

export function LocationLoginPage(): ReactNode {
  const { code = '' } = useParams<{ code: string }>();
  const [params] = useSearchParams();
  /*
    Where the worker was going before they were asked to sign in.

    Carried through the two links out of this page as well. A counter opening
    `/w/<branchId>` is routed here, and somebody who turns out to have a
    personal account rather than a counter password clicks "sign in here" —
    dropping `next` there meant their own link stopped meaning anything and
    they landed on CenoX Home.
  */
  const next = safeNext(params.get('next'));
  const { locationLogin, user } = useAuth();
  const navigate = useNavigate();

  const [context, setContext] = useState<LocationSignInContext | null>(null);
  const [unknownLink, setUnknownLink] = useState(false);
  const [username, setUsername] = useState(params.get('u') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<LocationSignInContext>(`/auth/location/${encodeURIComponent(code)}`)
      .then(
        (ctx) => { if (!cancelled) setContext(ctx); },
        () => { if (!cancelled) setUnknownLink(true); },
      );
    return () => { cancelled = true; };
  }, [code]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await locationLogin(code, username, password);
      /*
        Back where they were going, when they were going somewhere.

        A worker handed `/w/<branchId>` arrives here because that link routed
        them there, and landing them on a general screen instead would mean the
        link they were given never reaches what it names. `next` is validated
        by `safeNext` — same-origin paths only, never back to an auth screen —
        so it cannot be turned into an open redirect.

        Without one, straight to the day's work: `/business` resolves a
        location login to its own location's workspace, the same place the link
        sends it next time.
      */
      navigate(next ?? LOCATION_HOME, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not sign in. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (unknownLink) {
    return (
      <AuthLayout title="Link not recognised" subtitle="This sign-in link is not valid">
        <p className="text-sm text-slate-600">
          Ask the owner for a current link, or{' '}
          <Link to={withNext('/login', next)} className="font-medium text-brand-700 hover:underline">
            sign in with your own account
          </Link>
          .
        </p>
      </AuthLayout>
    );
  }

  if (!context) {
    return (
      <AuthLayout title="Location sign-in" subtitle="Checking the link">
        <Spinner />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={context.businessName} subtitle="Sign in to this location">
      <form className="space-y-4" onSubmit={handleSubmit}>
        <Input
          label="Username"
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="holyghost"
          autoComplete="username"
          autoFocus={username === ''}
        />
        <Input
          label="Password"
          required
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          autoFocus={username !== ''}
        />

        {error && (
          <p role="alert" className="text-sm font-medium text-red-600">
            {error}
          </p>
        )}

        {user && (
          <p className="text-xs text-slate-500">
            You are signed in as {user.fullName}. Signing in here switches this browser to the
            location.
          </p>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          Sign in
        </Button>

        <p className="text-center text-xs text-slate-500">
          Signing in as a location. For your own account,{' '}
          <Link to={withNext('/login', next)} className="font-medium text-brand-700 hover:underline">
            sign in here
          </Link>
          .
        </p>
      </form>
    </AuthLayout>
  );
}
